import { Ionicons } from '@expo/vector-icons';
import { router } from 'expo-router';
import React, { useCallback, useState } from 'react';
import { Pressable, StyleSheet, View } from 'react-native';

import { ApproveConfirmModal } from './approve-confirm-modal';

import { Text } from '@/components/ui';
import { useResolveFollowUp } from '@/hooks/use-data';
import type { TodoItem, TodoKind } from '@/hooks/use-todo-items';
import { useBulkApproveWorkOrders } from '@/hooks/use-work-order-review';
import { Alert } from '@/utils/alert';
import { RETURNED_FOR_REVIEW_COLOR } from '@/utils/approval-state';
import { formatMoneyCents } from '@/utils/format-money';

/**
 * One To Do row, with its in-place action (#279).
 *
 * Shared by the dashboard panel and the full To Do page so the two cannot drift — the same
 * discipline `useTodoItems` applies to the data. A row that behaved differently on the page
 * from the panel would be the 280/86/0 problem in miniature.
 *
 * ── ACTIONABLE IN PLACE ──
 *
 * Approvals get Review (which opens the approve popup) and follow-ups get Done, on the row. The point of a to-do list is
 * to shorten it; making someone open a detail page to clear a one-click item is how a list
 * stops getting cleared. Review rows get no button: writing a customer-facing description
 * is not a one-click action, so they open the work order.
 */

/**
 * Icons say WHAT KIND OF THING the row is, not what you do to it. Work-order rows carry the
 * hammer-and-wrench the rest of the app uses for work orders; task rows (follow-ups and
 * standalone tasks) carry the checkmark the Tasks section uses. An earlier version gave the
 * approval row a checkmark, which made an approval read as a task and a task as nothing in
 * particular.
 */
export const TODO_KIND_META: Record<TodoKind, {
	icon: keyof typeof Ionicons.glyphMap;
	color: string;
	label: string;
	/** True for rows that are TASKS rather than work orders — they get the green tint. */
	isTask: boolean;
}> = {
	approval: { icon: 'construct-outline', color: '#ef6c00', label: 'Approval', isTask: false },
	// Accounting's step: write the customer-facing description and hand it on.
	review: { icon: 'create-outline', color: '#6d4c41', label: 'Review', isTask: false },
	/**
	 * A review row the PM sent back (#503). "Rejected" is the word accounting uses for it,
	 * and red because somebody is waiting — the same red as overdue, deliberately, so the
	 * top of the list reads as one band of urgency rather than two competing colours.
	 */
	returned: {
		icon: 'arrow-undo-circle-outline', color: RETURNED_FOR_REVIEW_COLOR, label: 'Rejected', isTask: false,
	},
	'follow-up': { icon: 'checkmark-circle-outline', color: '#e65100', label: 'Follow-up', isTask: true },
	task: { icon: 'checkmark-circle-outline', color: '#546e7a', label: 'Task', isTask: true },
	/**
	 * Mail, and it carries the envelope the inbox uses everywhere else — a row that is
	 * actually a message must not look like a work order somebody forgot to categorise.
	 *
	 * Triage is red for the same reason `returned` is: somebody sent this in and nobody has
	 * owned it yet, which is a person waiting rather than a deadline approaching. Assigned
	 * mail is the ordinary inbox blue.
	 */
	'inbox-triage': { icon: 'alert-circle-outline', color: '#c62828', label: 'Triage', isTask: false },
	'inbox-assigned': { icon: 'mail-outline', color: '#1976d2', label: 'Inbox', isTask: false },
};

export const TodoRow: React.FC<{
	item: TodoItem;
	onChanged: () => void | Promise<unknown>;
}> = ({ item, onChanged }) => {
	const meta = TODO_KIND_META[item.kind];
	const returned = item.kind === 'returned';
	const [busy, setBusy] = useState(false);
	const approve = useBulkApproveWorkOrders();
	const resolveFollowUp = useResolveFollowUp();

	const open = useCallback(() => {
		// Mail opens the MESSAGE. Falling through to /tasks would strand someone on a page
		// that has nothing to do with the row they clicked.
		if (item.messageId) {
			router.push({ pathname: '/inbox/[id]', params: { id: item.messageId } });
		} else if (item.workOrderId) {
			router.push({ pathname: '/work-orders/[id]', params: { id: item.workOrderId } });
		} else {
			router.push('/tasks');
		}
	}, [item.messageId, item.workOrderId]);

	const doApprove = useCallback(async () => {
		if (!item.workOrderId) return;
		setBusy(true);
		try {
			const result = await approve.mutateAsync([item.workOrderId]);
			if (result.errors.length > 0) Alert.alert('Could not approve', result.errors[0].error);
			await onChanged();
		} catch (e) {
			Alert.alert('Could not approve', (e as Error).message);
		} finally {
			setBusy(false);
		}
	}, [item.workOrderId, approve, onChanged]);

	/**
	 * EVERY in-place action on this list confirms first, and says what it is about to do.
	 *
	 * Approve releases a work order to billing — an association gets invoiced for it — from a
	 * button that sits a few pixels from Done on the row above. One stray tap must not bill a
	 * customer. The confirmation is a structured modal (ApproveConfirmModal), not an Alert
	 * string: it carries two markdown descriptions and an amount, which a text dialog turned
	 * into an unreadable wall.
	 */
	const [confirmingApprove, setConfirmingApprove] = useState(false);
	const onApprove = useCallback(() => {
		if (item.workOrderId) setConfirmingApprove(true);
	}, [item.workOrderId]);
	const confirmApprove = useCallback(async () => {
		await doApprove();
		setConfirmingApprove(false);
	}, [doApprove]);

	const onResolve = useCallback(() => {
		if (!item.taskId) return;
		Alert.alert(
			'Mark follow-up done?',
			`${item.title.replace('Follow up on ', '')}${item.subtitle ? ` · ${item.subtitle}` : ''}\n`
				+ `${item.preview ? `"${item.preview}"\n\n` : '\n'}`
				+ 'This clears the follow-up flag and removes the task from your To Do list.',
			[
				{ text: 'Cancel', style: 'cancel' },
				{ text: 'Mark done', onPress: () => resolveFollowUp.mutate(item.taskId!) },
			],
		);
	}, [item.taskId, item.title, item.subtitle, item.preview, resolveFollowUp]);

	return (
		<View style={[styles.row, meta.isTask && styles.rowTask]} testID={`v2-todo-${item.id}`}>
			<Ionicons name={meta.icon} size={18} color={meta.color} />
			<Pressable onPress={open} style={styles.main}>
				<Text numberOfLines={1} style={[styles.title, returned && styles.titleReturned]}>
					{item.title}
					{/*
						The cost, on the title line — it is half the approval decision, and
						having to open each work order to find it is what made the paper sheet
						faster than the app.

						Rendered only when the API supplied a figure: `costCents == null` means
						unknown, and printing "$0.00" for unknown on a row somebody is about to
						approve would be a wrong number presented as fact.
					*/}
					{item.costCents != null ? (
						<Text style={styles.cost}>{'  —  '}{formatMoneyCents(item.costCents)}</Text>
					) : null}
				</Text>
				{/*
					Location, then the description of work completed dimmed after it — the
					email-preheader pattern: enough of the content to recognise the item
					without opening it.

					ONE `Text` with nested spans, not two rows, so the pair truncates as a
					unit: the location always survives and the prose is what gets clipped.
					Two sibling Texts would each truncate independently and cost a line.
				*/}
				{(item.associationName || item.subtitle || item.preview) ? (
					<Text numberOfLines={1} style={styles.sub}>
						{/* Association first: it is the answer to "whose money is this", which the location alone does not give. */}
						{item.associationName}
						{item.associationName && item.subtitle ? <Text style={styles.dim}>{'  ·  '}</Text> : null}
						{item.subtitle}
						{(item.associationName || item.subtitle) && item.preview ? <Text style={styles.dim}>{'  ·  '}</Text> : null}
						{/*
							On a sent-back row the preview IS the PM's reason, so it is not dimmed:
							it is the one thing accounting has to read before opening the job.
						*/}
						{item.preview ? (
							<Text style={returned ? styles.reason : styles.dim}>{item.preview}</Text>
						) : null}
					</Text>
				) : null}
				<Text style={[styles.due, (item.overdue || returned) && styles.dueOverdue]}>
					{meta.label} · {item.dueLabel}
				</Text>
			</Pressable>

			{/*
				Labelled REVIEW, not Approve: it opens the review popup, and approving is a
				separate, explicit click inside it. "Approve" on the row read as an automatic
				one-click approval.
			*/}
			{item.kind === 'approval' && (
				<Pressable
					onPress={onApprove}
					disabled={busy}
					style={[styles.action, styles.actionPrimary]}
					testID={`v2-todo-approve-${item.workOrderId}`}
				>
					<Text style={styles.actionPrimaryLabel}>{busy ? '…' : 'Review'}</Text>
				</Pressable>
			)}
			{item.kind === 'follow-up' && (
				<Pressable onPress={onResolve} style={styles.action} testID={`v2-todo-done-${item.taskId}`}>
					<Text style={styles.actionLabel}>Done</Text>
				</Pressable>
			)}
			{/* A sent-back row is still a review: same button, because the rewrite is still the job. */}
			{(item.kind === 'review' || returned) && (
				<Pressable onPress={open} style={styles.action} testID={`v2-todo-review-${item.workOrderId}`}>
					<Text style={styles.actionLabel}>Review</Text>
				</Pressable>
			)}
			<ApproveConfirmModal
				item={confirmingApprove ? item : null}
				busy={busy}
				onCancel={() => setConfirmingApprove(false)}
				onConfirm={() => void confirmApprove()}
			/>
		</View>
	);
};

const styles = StyleSheet.create({
	row: {
		flexDirection: 'row',
		alignItems: 'center',
		gap: 10,
		paddingVertical: 10,
		borderBottomWidth: 1,
		borderBottomColor: '#f1f3f4',
	},
	/**
	 * Task rows: a very slight green wash so a mixed list reads as two kinds of thing at a
	 * glance. Faint on purpose — it is a category marker, not a status; the row's own
	 * colours (overdue red, the Approve button's green) still have to win against it.
	 */
	rowTask: { backgroundColor: '#f4fbf4', marginHorizontal: -4, paddingHorizontal: 4, borderRadius: 4 },
	// minWidth 0 so a long title truncates instead of pushing the action off the card —
	// RN-Web flex children default to min-width:auto.
	main: { flex: 1, minWidth: 0 },
	title: { fontSize: 14, fontWeight: '600', color: '#263238' },
	titleReturned: { color: RETURNED_FOR_REVIEW_COLOR },
	// The PM's reason: full weight and the row's own red, not the dimmed preview grey.
	reason: { color: RETURNED_FOR_REVIEW_COLOR, fontWeight: '600' },
	// Same weight as the title it sits on, tabular so a column of figures lines up.
	cost: { color: '#2e7d32', fontVariant: ['tabular-nums'] },
	/**
	 * The location: darkened to blue-grey 800.
	 *
	 * Not for its own sake — it buys the preview beside it room to be lighter AND still
	 * legible. At the previous pairing the location was 5.4:1 on white, which left nothing
	 * between it and the 4.5:1 floor for the dimmer text.
	 */
	sub: { fontSize: 12, color: '#37474f', marginTop: 1 },
	/**
	 * The description preview: lighter than the location, but READABLE.
	 *
	 * Was #b0bec5, which measures 1.91:1 on white — barely a third of the 4.5:1 WCAG AA
	 * floor for text this size, and why it read as washed out rather than merely secondary.
	 * #5d7986 is 4.62:1: the lightest blue-grey that still clears AA, so it stays clearly
	 * subordinate to the 9.65:1 location without being decoration.
	 */
	dim: { color: '#5d7986' },
	due: { fontSize: 12, color: '#90a4ae', marginTop: 2 },
	dueOverdue: { color: '#c62828', fontWeight: '700' },
	action: {
		paddingHorizontal: 10, paddingVertical: 6,
		borderRadius: 6, borderWidth: 1, borderColor: '#cfd8dc',
	},
	actionLabel: { fontSize: 12, color: '#546e7a', fontWeight: '600' },
	actionPrimary: { backgroundColor: '#2e7d32', borderColor: '#2e7d32' },
	actionPrimaryLabel: { fontSize: 12, color: '#fff', fontWeight: '700' },
});
